<!-- WARNING: This component MUST be kept in sync with swapcard_session_detail.handlebars -->
<!-- The Handlebars partial is the SEO/server-rendered version of this component.          -->
<!-- Any changes to layout, fields, or structure here MUST be mirrored in:                 -->
<!--   src/Templates/Default/Shared/Partials/Swapcard/swapcard_session_detail.handlebars  -->
<template lang="pug">
.session-detail
  p.session-detail__loading(v-if="loading") Loading session...
  .session-detail__error(v-else-if="error")
    p {{ error }}
    button.session-detail__back-btn(type="button" @click="goBack")
      | ← Back to Agenda
  template(v-else-if="session")
    .session-detail__nav
      button.session-detail__back-btn(type="button" @click="goBack")
        | ← Back to Agenda
    .session-detail__layout
      //- Main column
      .session-detail__main
        //- Session card
        .session-detail__card
          .session-detail__eyebrow Viewing Session Detail
          h1.session-detail__title {{ session.title }}

          //- Metadata row
          .session-detail__meta-row
            .session-detail__meta-item(v-if="session.startTime")
              svg.session-detail__meta-icon(viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true")
                rect(x="2" y="3" width="12" height="12" rx="1.5" stroke="currentColor" stroke-width="1.5")
                path(d="M2 7h12M5 1v2M11 1v2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round")
              time(:datetime="session.startTime") {{ formatDate(session.startTime) }}
            .session-detail__meta-item(v-if="session.startTime")
              svg.session-detail__meta-icon(viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true")
                circle(cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5")
                path(d="M8 5v3.5l2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round")
              span
                time(:datetime="session.startTime") {{ formatTime(session.startTime) }}
                template(v-if="session.endTime")
                  |  –
                  time(:datetime="session.endTime") {{ formatTime(session.endTime) }}
            .session-detail__meta-item(v-if="session.room")
              img.session-detail__meta-icon(src="/img/icons/schedule/sm/location.svg" width="14" height="14" aria-hidden="true" alt="")
              span {{ session.room }}

          //- Tag chips — strict order: Format, Pre-registration, Credit, Topics
          .session-detail__tags(v-if="hasTags")
            span.session-detail__tag.session-detail__tag--type(v-if="session.sessionFormat") {{ session.sessionFormat }}
            span.session-detail__tag.session-detail__tag--prereg(v-if="session.preregRequired") Pre-registration Required
            span.session-detail__tag.session-detail__tag--credit(v-if="creditType") {{ creditType }}
            span.session-detail__tag.session-detail__tag--topic(
              v-for="topic in session.topics"
              :key="topic") {{ topic }}

          //- Description
          .session-detail__description(v-if="session.description" v-html="sanitizedDescription")

        //- Speakers
        .session-detail__speakers-section(v-if="session.speakers && session.speakers.length")
          .session-detail__speakers-heading
            svg.session-detail__speaker-icon(viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true")
              path(d="M8 8a3 3 0 100-6 3 3 0 000 6zM2 14c0-2.761 2.686-5 6-5s6 2.239 6 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round")
            span {{ session.speakers.length === 1 ? 'Speaker' : 'Speakers' }}
          SessionSpeakerCard(
            v-for="(speaker, i) in session.speakers"
            :key="speaker.id"
            :speaker="speaker"
            :color-index="i")

        //- Access Your Session Tools
        .session-detail__tools
          .session-detail__tools-heading Access Your Session Tools
          .session-detail__tools-subheading Build Your Personalized Schedule
          .session-detail__tools-body
            p(v-if="hasAppConfig") To personalize your experience, please sign in to your account or open the My TASB Events app. Once logged in, you can add or remove this session from your schedule, bookmark it, and access related materials.
            p(v-else) To personalize your experience, please sign in to your account to visit My TASB Events. Once logged in, you can add or remove this session from your schedule, bookmark it, and access related materials. An app will be available in the near future.
          .session-detail__tools-actions
            a.session-detail__tools-login-btn(
              :href="resolvedLoginUrl"
              target="_blank"
              rel="noopener noreferrer") Log In
            a.session-detail__tools-download-btn(
              v-if="iosDownloadUrl"
              :href="iosDownloadUrl"
              target="_blank"
              rel="noopener") Download App for iOS
            a.session-detail__tools-download-btn(
              v-if="androidDownloadUrl"
              :href="androidDownloadUrl"
              target="_blank"
              rel="noopener") Download App for Android

        //- Back button — bottom
        button.session-detail__back-btn(type="button" @click="goBack")
          | ← Back to Agenda

      //- Sidebar
      SessionDetailSidebar(:session="session")
</template>

<script>
import SessionSpeakerCard from './SessionSpeakerCard.vue';
import SessionDetailSidebar from './SessionDetailSidebar.vue';
import { scheduleEventBus, SCHEDULE_EVENTS } from './scheduleEventBus';
import { formatTime, formatDate } from './scheduleTimeUtils.js';
import { sanitizeHtml } from './sanitizeHtml.js';

export default {
  name: 'SessionDetail',

  components: {
    SessionSpeakerCard,
    SessionDetailSidebar,
  },

  props: {
    swapcardSessionId: {
      type: String,
      required: true,
    },
    parentContentItemCodename: {
      type: String,
      required: true,
    },
    parentPageUrl: {
      type: String,
      required: true,
    },
    scheduleTabCodename: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      session: null,
      loading: false,
      error: null,
    };
  },

  computed: {
    iosDownloadUrl() {
      return this.$root.swapcardAppDownloadUrlIos || '';
    },

    androidDownloadUrl() {
      return this.$root.swapcardAppDownloadUrlAndroid || '';
    },

    creditType() {
      if (!this.session) return '';
      const value = this.session.creditType || '';
      if (!value || value === 'Not for Credit') return '';
      return value;
    },

    hasTags() {
      if (!this.session) return false;
      return (
        this.session.sessionFormat ||
        (this.session.topics && this.session.topics.length) ||
        this.creditType ||
        this.session.preregRequired
      );
    },

    resolvedLoginUrl() {
      const sessionUrl = (this.session && this.session.sessionUrl) || '';
      if (sessionUrl) return sessionUrl;
      const slug = this.$root.eventSlug || '';
      if (!slug) return '#';
      return `https://myevents.tasb.org/login?redirect_url=${encodeURIComponent(`https://myevents.tasb.org/event/${slug}`)}`;
    },

    hasAppConfig() {
      return !!(this.iosDownloadUrl || this.androidDownloadUrl);
    },

    sanitizedDescription() {
      return sanitizeHtml(this.session && this.session.description);
    },
  },

  watch: {
    swapcardSessionId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchSession();
      },
    },
  },

  methods: {
    async fetchSession() {
      this.loading = true;
      this.error = null;
      this.session = null;

      try {
        const url = `/api/v1/swapcard/${encodeURIComponent(this.parentContentItemCodename)}/sessions/${encodeURIComponent(this.swapcardSessionId)}`;
        const response = await fetch(url, { credentials: 'same-origin' });

        if (!response.ok) {
          this.error = `Failed to load session (${response.status}).`;
          return;
        }

        this.session = await response.json();
        this.$emit('session-loaded');
      } catch (err) {
        this.error = 'Failed to load session. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    goBack() {
      if (history.state && history.state.fromListing) {
        history.back();
        return;
      }
      const url = this.scheduleTabCodename
        ? `${this.parentPageUrl}#${this.scheduleTabCodename}`
        : this.parentPageUrl;
      history.replaceState({}, '', url);
      scheduleEventBus.$emit(SCHEDULE_EVENTS.NAVIGATE_LISTING);
    },

    openModal() {
      scheduleEventBus.$emit(SCHEDULE_EVENTS.OPEN_MODAL, { sessionUrl: (this.session && this.session.sessionUrl) || '' });
    },

    formatDate,

    formatTime,

  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

// ---- Root ----
.session-detail {
  font-family: 'Public Sans', sans-serif;
  background: rgb(247, 247, 248);
}

// ---- Layout ----
.session-detail__nav {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px 0;
}

.session-detail__layout {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 20px 24px;
}

.session-detail__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// ---- States ----
.session-detail__loading,
.session-detail__error {
  padding: 40px 20px;
  color: $color-text-secondary;
  font-size: 16px;
}

// ---- Back button ----
.session-detail__back-btn {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  background: white;
  border: 1px solid $color-primary;
  border-radius: 8px;
  color: $color-text-dark;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  box-shadow: $shadow-btn;

  &:hover {
    background: $color-btn-outline-hover-bg;
    border-color: $color-btn-outline-hover-border;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}

// ---- Session card ----
.session-detail__card {
  background: white;
  border-radius: 12px;
  box-shadow: $shadow-card;
  padding: 40px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.session-detail__eyebrow {
  font-size: 10px;
  font-weight: 700;
  color: $color-text-secondary;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1;
}

.session-detail__title {
  font-size: 26px;
  font-weight: 700;
  color: $color-text-dark;
  line-height: 1.3;
  letter-spacing: -0.01em;
  margin: 0;
}

// ---- Metadata row ----
.session-detail__meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.session-detail__meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 500;
  color: $color-text-secondary;
  line-height: 1;
}

.session-detail__meta-icon {
  color: $color-text-secondary;
  flex-shrink: 0;
}

// ---- Tags ----
.session-detail__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.session-detail__tag {
  height: 24px;
  padding: 4px 8px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;

  &--type {
    background: $color-tag-type-bg;
    color: $color-tag-type-fg;
  }

  &--topic {
    background: $color-tag-topic-bg;
    color: $color-tag-topic-fg;
  }

  &--credit {
    background: $color-tag-credit-bg;
    color: $color-tag-credit-fg;
  }

  &--prereg {
    background: $color-tag-prereg-bg;
    color: $color-tag-prereg-fg;
  }
}

// ---- Description ----
.session-detail__description {
  font-size: 16px;
  font-weight: 400;
  color: $color-text-secondary;
  line-height: 1.5;

  ::v-deep p {
    margin: 0 0 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ::v-deep .tm-symbol {
    font-size: 0.6em;
    vertical-align: 0;
    line-height: 0;
  }
}

// ---- Speakers section ----
.session-detail__speakers-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.session-detail__speakers-heading {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 700;
  color: $color-text-dark;
}

.session-detail__speaker-icon {
  color: $color-text-dark;
  flex-shrink: 0;
}

// ---- Session tools ----
.session-detail__tools {
  background: white;
  border: 1px solid $color-border;
  border-radius: 12px;
  padding: 32px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.session-detail__tools-heading {
  font-size: 16px;
  font-weight: 700;
  color: $color-text-dark;
  line-height: 1.5;
}

.session-detail__tools-subheading {
  font-size: 18px;
  font-weight: 700;
  color: $color-text-secondary;
  line-height: 1.35;
  letter-spacing: -0.01em;
}

.session-detail__tools-body {
  font-size: 16px;
  font-weight: 400;
  color: $color-text-secondary;
  line-height: 1.5;

  p {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.session-detail__tools-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.session-detail__tools-login-btn {
  display: inline-flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  background: $color-cta-accent;
  color: $color-cta-text;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-decoration: none;
  box-shadow: $shadow-btn-subtle;

  &:hover {
    background: $color-cta-accent-hover;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}

.session-detail__tools-download-btn {
  height: 40px;
  padding: 0 12px;
  background: white;
  border: 1px solid $color-primary;
  border-radius: 8px;
  color: $color-text-dark;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  box-shadow: $shadow-btn;

  &:hover {
    background: $color-btn-outline-hover-bg;
    border-color: $color-btn-outline-hover-border;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}

@media (max-width: 1023px) {
  .session-detail__layout {
    flex-direction: column;
  }
}
</style>
